/**
 * Add or remove a tool from My Stack, from anywhere a tool is shown.
 *
 * Deliberately understated: a plus that becomes a tick. The card's primary
 * action is "See how to start", and this must not compete with it, so it is
 * an icon button with an accessible name rather than a second filled button.
 *
 * The directory is a legitimate way into a stack without the quiz. Someone who
 * already knows they want Canva should not have to answer two questions to
 * keep it, so `intentId` is null on this path and My Stack simply omits the
 * "matched for" line rather than inventing a reason.
 *
 * Signed out this writes to localStorage, so the control works before an
 * account exists and the saves are adopted on signup.
 */

import { Check, Plus } from "lucide-react";
import { useStack } from "@/hooks/useStack";

interface SaveToolButtonProps {
  toolId: string;
  /** The tool's display name, so the accessible label names the thing. */
  toolName: string;
  /** Set when the save came from a quiz result rather than the directory. */
  intentId?: string | null;
  className?: string;
}

export default function SaveToolButton({
  toolId,
  toolName,
  intentId = null,
  className = "",
}: SaveToolButtonProps) {
  const stack = useStack();
  const saved = stack.isSaved(toolId);

  const label = saved
    ? `Remove ${toolName} from My Stack`
    : `Add ${toolName} to My Stack`;

  return (
    <button
      type="button"
      onClick={event => {
        // Cards are often inside a link; keeping this from bubbling means
        // adding a tool never navigates you away from the grid.
        event.preventDefault();
        event.stopPropagation();
        if (saved) stack.removeTool(toolId);
        else stack.saveTool(toolId, intentId);
      }}
      aria-pressed={saved}
      aria-label={label}
      title={label}
      disabled={!stack.ready}
      // text-sm/font-semibold to match the shared button system. It was
      // inheriting 16px/400 from the card, so the hover label came out a size
      // larger and a weight lighter than every other button label on the page.
      className={`group/save inline-flex items-center justify-center gap-1.5 h-11 min-w-11 px-2.5 rounded-md border text-sm font-semibold transition-colors disabled:opacity-40 ${
        saved
          ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
          : "border-border text-muted-foreground hover:border-primary hover:text-primary"
      } ${className}`}
    >
      {saved ? (
        <Check className="w-4 h-4 shrink-0" />
      ) : (
        <Plus className="w-4 h-4 shrink-0" />
      )}
      {/* Hidden until hover or focus on a pointer device, always present for
          screen readers via aria-label. On touch there is no hover, so the
          icon plus the 44px target has to carry it on its own. */}
      <span className="hidden lg:inline-block max-w-0 overflow-hidden whitespace-nowrap text-sm font-semibold opacity-0 transition-all duration-150 group-hover/save:max-w-[9rem] group-hover/save:opacity-100 group-focus-visible/save:max-w-[9rem] group-focus-visible/save:opacity-100">
        {saved ? "In My Stack" : "Add to My Stack"}
      </span>
    </button>
  );
}
